import { useEffect, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { Button, Form, Typography } from 'antd';

import { InvoiceMapping } from '~apis/mapping';
import { formatNumByTaxInvoice } from '~components/Settlement/utils';
import { css } from '~libs/emotion';
import { TaxInvoiceType } from '~models/tax-invoice';

import useTransactionMappingStore from './TransactionMappingStore';

interface Props {
  item: InvoiceMapping;
  type: TaxInvoiceType;
  taxInvoiceId: string;
}

const MappingTaxInvoiceBtn: React.FC<Props> = ({ item, type, taxInvoiceId }) => {
  const [price, setPrice] = useState<number>(formatNumByTaxInvoice(item.unmappedAmount, type) ?? 0);

  const { addMappedInvoice, mappedInvoices, originalMappedInvoices } = useTransactionMappingStore();
  const taxInvoiceMapped = mappedInvoices.find((invoice) => invoice.taxInvoiceId === taxInvoiceId);
  const targetMappedAmount =
    originalMappedInvoices.find((invoice) => invoice.taxInvoiceId === taxInvoiceId)?.mappedAmount ??
    0;

  const targetUnMappedAmount = taxInvoiceMapped
    ? formatNumByTaxInvoice(item.unmappedAmount, type)
    : formatNumByTaxInvoice(item.unmappedAmount, type) + targetMappedAmount;

  const getStatus = () => {
    if (
      item.unmappedAmount === 0 &&
      !originalMappedInvoices.map((i) => i.taxInvoiceId).includes(item.taxInvoiceId)
    ) {
      return 'COMPLETED';
    }

    if (mappedInvoices.some((i) => i.taxInvoiceId === item.taxInvoiceId)) {
      return 'SELECTED';
    }

    return 'NOT_SELECTED';
  };

  const status = getStatus();

  useEffect(() => {
    if (item) {
      setPrice(formatNumByTaxInvoice(item.unmappedAmount, type) ?? 0);
    }
  }, [item]);

  useEffect(() => {
    if (!taxInvoiceMapped) {
      setPrice((formatNumByTaxInvoice(item.unmappedAmount, type) ?? 0) + targetMappedAmount);
    }
  }, [taxInvoiceMapped]);

  const onSelect = (price: number) => {
    addMappedInvoice({
      counterPartyName: item.counterPartyName,
      taxInvoiceType: item.taxInvoiceType,
      counterPartyCompanyName: item.counterPartyCompanyName,
      counterPartyBusinessRegNumber: item.counterPartyBusinessRegNumber,
      repItems: item.repItems,
      totalAmount: item.totalAmount,
      mappedAmount: price,
      categoryLabelKey: null,
      groupLabelId: null,
      reportingDate: item.reportingDate,
      taxInvoiceId: item.taxInvoiceId,
      transactionLabelAmount: item.totalAmount * (item.taxInvoiceType === 'PURCHASE' ? -1 : 1),
      businessEntityId: item.businessEntityId,
    });
  };

  if (status === 'COMPLETED') {
    return (
      <Flex
        gap={4}
        align="center"
        style={{
          width: 'fit-content',
        }}
      >
        <Typography.Text
          disabled
          style={{
            width: 160,
          }}
        >{`${Math.abs(item.mappingAmount).toLocaleString()}원`}</Typography.Text>
        <Typography.Text
          disabled
          style={{
            width: 'max-content',
          }}
        >
          정산 완료
        </Typography.Text>
      </Flex>
    );
  }

  if (status === 'SELECTED') {
    return (
      <Flex
        gap={4}
        align="center"
        style={{
          width: 'fit-content',
        }}
      >
        <Typography.Text
          disabled
          style={{
            width: 160,
          }}
        >{`${(mappedInvoices || [])
          .find((m) => m.taxInvoiceId === item.taxInvoiceId)
          ?.mappedAmount.toLocaleString()}원`}</Typography.Text>
        <Typography.Text
          disabled
          style={{
            width: 'max-content',
          }}
        >
          추가됨
        </Typography.Text>
      </Flex>
    );
  }

  const triggerPriceValidation = price > Math.abs(targetUnMappedAmount);

  return (
    <Flex
      gap={4}
      align="flex-start"
      style={{
        width: 'fit-content',
      }}
    >
      <Form.Item
        validateStatus={triggerPriceValidation ? 'error' : undefined}
        help={triggerPriceValidation ? '미정산 잔액 이하로 입력하세요.' : undefined}
      >
        <FormattedInput
          style={{
            width: 160,
            textAlign: 'right',
          }}
          className={css`
            & input {
              text-align: right;
            }
          `}
          defaultValue={price + ''}
          value={price + ''}
          onChange={(value) => {
            setPrice(Number(value));
          }}
          suffix="원"
          formatter={(value: string) => {
            if (!value || isNaN(parseInt(value, 10))) return '';
            return parseInt(value, 10).toLocaleString();
          }}
          deformatter={(value: string) => {
            if (!value || isNaN(parseInt(value, 10))) return '';
            return value.replace(/,/g, '');
          }}
        />
      </Form.Item>

      <Button
        disabled={triggerPriceValidation}
        onClick={() => {
          onSelect(price);
        }}
      >
        추가
      </Button>
    </Flex>
  );
};

export default MappingTaxInvoiceBtn;
